import React, { useEffect, useMemo, useState } from 'react';
import ConstructionLayout from './ConstructionLayout';
import TablePagination from './TablePagination';
import { useModule } from '../../contexts/ModuleContext';
import { useTheme } from '../../contexts/ThemeContext';
import { fetchObservationsSummary } from '../../services/observationsService';
import { SITE_VISUALS_BASE } from '../../data/siteVisualsMedia';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  FileText,
  ImageOff,
  Info,
  Leaf,
  MapPin,
  Maximize2,
  MoreHorizontal,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';

const BUCKET_META = {
  Safety: { color: '#EF4444', bg: 'rgba(239, 68, 68, 0.12)', icon: AlertTriangle },
  Info: { color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.12)', icon: Info },
  Plantation: { color: '#22C55E', bg: 'rgba(34, 197, 94, 0.12)', icon: Leaf },
  'Reserved Land': { color: '#A855F7', bg: 'rgba(168, 85, 247, 0.12)', icon: MapPin },
  Other: { color: '#9CA3AF', bg: 'rgba(156, 163, 175, 0.12)', icon: MoreHorizontal },
};

/** Resolve observation_details file/preview paths to a browser URL. */
export function resolveObservationMediaUrl(path) {
  if (!path || typeof path !== 'string') return null;
  const trimmed = path.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('data:')) return trimmed;
  if (trimmed.startsWith('/')) return trimmed;
  const encoded = trimmed
    .split('/')
    .filter(Boolean)
    .map(encodeURIComponent)
    .join('/');
  return `${SITE_VISUALS_BASE}/${encoded}`;
}

function formatLocation(item) {
  const loc = item?.location;
  if (loc && loc.latitude != null && loc.longitude != null) {
    return `${Number(loc.latitude).toFixed(6)}, ${Number(loc.longitude).toFixed(6)}`;
  }
  if (item?.geometry) return item.geometry;
  return '—';
}

function categoryStyle(bucket) {
  const meta = BUCKET_META[bucket] || BUCKET_META.Other;
  return {
    color: meta.color,
    backgroundColor: meta.bg,
    border: `1px solid ${meta.color}55`,
  };
}

function statusStyle(status, isDarkMode) {
  const s = String(status || '').toLowerCase();
  if (s.includes('review') || s.includes('open') || s.includes('required')) {
    return {
      color: '#FBBF24',
      backgroundColor: isDarkMode ? 'rgba(251, 191, 36, 0.15)' : 'rgba(251, 191, 36, 0.12)',
      border: '1px solid rgba(251, 191, 36, 0.35)',
    };
  }
  if (s.includes('closed') || s.includes('resolved') || s.includes('done')) {
    return {
      color: '#22C55E',
      backgroundColor: isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.12)',
      border: '1px solid rgba(34, 197, 94, 0.35)',
    };
  }
  return {
    color: isDarkMode ? '#E5E7EB' : '#374151',
    backgroundColor: isDarkMode ? 'rgba(156, 163, 175, 0.15)' : 'rgba(156, 163, 175, 0.12)',
    border: '1px solid rgba(156, 163, 175, 0.35)',
  };
}

export default function SiteObservationsPage() {
  const { isDarkMode } = useTheme();
  const { activeProject, activeSurvey } = useModule();

  const [summary, setSummary] = useState({ totalCount: 0, categories: [], items: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeBucket, setActiveBucket] = useState('All');
  const [selectedId, setSelectedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const projectId = activeProject?.numericId;
  const surveyId = activeSurvey?.id;

  useEffect(() => {
    let isMounted = true;
    if (!projectId || !surveyId) {
      setSummary({ totalCount: 0, categories: [], items: [] });
      setLoading(false);
      setSelectedId(null);
      return;
    }

    setLoading(true);
    setCurrentPage(1);
    setActiveBucket('All');
    setSearch('');

    fetchObservationsSummary(projectId, surveyId)
      .then((res) => {
        if (!isMounted) return;
        setSummary(res);
        setSelectedId(res.items?.[0]?.id ?? null);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching observations:', err);
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [projectId, surveyId]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (summary.items || []).filter((item) => {
      if (activeBucket !== 'All' && item.uiBucket !== activeBucket) return false;
      if (!q) return true;
      const hay = `${item.name || ''} ${item.description || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [summary.items, activeBucket, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeBucket, search, rowsPerPage]);

  useEffect(() => {
    if (!filteredItems.length) {
      setSelectedId(null);
      return;
    }
    if (!filteredItems.some((i) => i.id === selectedId)) {
      setSelectedId(filteredItems[0].id);
    }
  }, [filteredItems, selectedId]);

  const totalPages = Math.ceil(filteredItems.length / rowsPerPage) || 1;
  const pageSafe = Math.min(currentPage, totalPages);
  const paginatedItems = filteredItems.slice(
    (pageSafe - 1) * rowsPerPage,
    pageSafe * rowsPerPage
  );

  const selectedIndex = filteredItems.findIndex((i) => i.id === selectedId);
  const selected = selectedIndex >= 0 ? filteredItems[selectedIndex] : null;

  const cardBg = isDarkMode ? '#111622' : '#FFFFFF';
  const cardBorder = isDarkMode ? '#1E2433' : '#E5E7EB';
  const muted = isDarkMode ? '#9CA3AF' : '#6B7280';
  const text = isDarkMode ? '#E5E7EB' : '#1F2937';
  const titleColor = isDarkMode ? '#F3F4F6' : '#111827';
  const headBg = isDarkMode ? '#161B26' : '#F9FAFB';
  const rowBorder = isDarkMode ? '#1E2433' : '#F3F4F6';
  const accent = isDarkMode ? '#EAB308' : '#CA8A04';

  const categoryCounts = useMemo(() => {
    const map = Object.fromEntries((summary.categories || []).map((c) => [c.category, c.count]));
    return map;
  }, [summary.categories]);

  const chipBuckets = ['All', 'Safety', 'Info', 'Plantation', 'Reserved Land', 'Other'];

  const distributionTotal = summary.totalCount || 0;

  return (
    <ConstructionLayout
      title="Site & Safety Observations"
      subtitle="Observations from drone imagery to help track site progress, safety, and compliance."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {!surveyId && (
          <div style={{ padding: '12px 16px', borderRadius: '8px', backgroundColor: headBg, color: muted, fontSize: '13px' }}>
            Select a survey date to load observations for this project.
          </div>
        )}

        {loading ? (
          <div
            style={{
              padding: '48px',
              textAlign: 'center',
              color: muted,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
            }}
          >
            <RefreshCw className="animate-spin" size={18} /> Loading observations…
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '12px',
              }}
            >
              <SummaryCard
                label="Total Observations"
                count={summary.totalCount}
                icon={FileText}
                color={muted}
                cardBg={cardBg}
                cardBorder={cardBorder}
                titleColor={titleColor}
                muted={muted}
                onClick={() => setActiveBucket('All')}
                active={activeBucket === 'All'}
                accent={accent}
              />
              {['Safety', 'Info', 'Plantation', 'Reserved Land', 'Other'].map((bucket) => {
                const meta = BUCKET_META[bucket];
                return (
                  <SummaryCard
                    key={bucket}
                    label={bucket}
                    count={categoryCounts[bucket] || 0}
                    icon={meta.icon}
                    color={meta.color}
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    titleColor={titleColor}
                    muted={muted}
                    onClick={() => setActiveBucket(bucket)}
                    active={activeBucket === bucket}
                    accent={accent}
                    showView
                  />
                );
              })}
            </div>

            {/* Distribution */}
            <div
              style={{
                backgroundColor: cardBg,
                border: `1px solid ${cardBorder}`,
                borderRadius: '12px',
                padding: '14px 16px',
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: 600, color: titleColor, marginBottom: '10px' }}>
                Observation distribution ({distributionTotal} total)
              </div>
              <div
                style={{
                  display: 'flex',
                  height: '10px',
                  borderRadius: '999px',
                  overflow: 'hidden',
                  backgroundColor: isDarkMode ? '#1E2433' : '#E5E7EB',
                }}
              >
                {distributionTotal > 0 &&
                  ['Safety', 'Info', 'Plantation', 'Reserved Land', 'Other'].map((bucket) => {
                    const count = categoryCounts[bucket] || 0;
                    if (!count) return null;
                    return (
                      <div
                        key={bucket}
                        title={`${bucket}: ${count}`}
                        style={{
                          width: `${(count / distributionTotal) * 100}%`,
                          backgroundColor: BUCKET_META[bucket].color,
                        }}
                      />
                    );
                  })}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '14px',
                  marginTop: '10px',
                  fontSize: '12px',
                  color: muted,
                }}
              >
                {['Safety', 'Info', 'Plantation', 'Reserved Land', 'Other'].map((bucket) => (
                  <span key={bucket} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: BUCKET_META[bucket].color,
                      }}
                    />
                    {bucket} ({categoryCounts[bucket] || 0})
                  </span>
                ))}
              </div>
            </div>

            {/* List + detail */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1.2fr) minmax(280px, 0.9fr)',
                gap: '20px',
                alignItems: 'start',
              }}
            >
              <div
                style={{
                  backgroundColor: cardBg,
                  border: `1px solid ${cardBorder}`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    padding: '14px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    borderBottom: `1px solid ${cardBorder}`,
                  }}
                >
                  <div style={{ fontSize: '14px', fontWeight: 700, color: titleColor }}>Observations</div>
                  <div style={{ position: 'relative', minWidth: '220px' }}>
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search observations..."
                      style={{
                        width: '100%',
                        padding: '8px 32px 8px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${cardBorder}`,
                        backgroundColor: isDarkMode ? '#0B0F17' : '#FFFFFF',
                        color: text,
                        fontSize: '13px',
                      }}
                    />
                    <Search
                      size={14}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: muted }}
                    />
                  </div>
                </div>

                <div style={{ padding: '10px 16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {chipBuckets.map((bucket) => {
                    const count =
                      bucket === 'All' ? summary.totalCount : categoryCounts[bucket] || 0;
                    const active = activeBucket === bucket;
                    return (
                      <button
                        key={bucket}
                        type="button"
                        onClick={() => setActiveBucket(bucket)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          border: `1px solid ${active ? accent : cardBorder}`,
                          backgroundColor: active ? (isDarkMode ? 'rgba(234, 179, 8, 0.12)' : 'rgba(202, 138, 4, 0.08)') : headBg,
                          color: active ? titleColor : muted,
                          fontSize: '12px',
                          fontWeight: active ? 700 : 500,
                          cursor: 'pointer',
                        }}
                      >
                        {bucket} ({count})
                      </button>
                    );
                  })}
                </div>

                {filteredItems.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: muted }}>
                    No observations for this survey.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto', minHeight: `calc(${rowsPerPage} * 44px + 48px)` }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ backgroundColor: headBg, borderBottom: `1px solid ${cardBorder}`, color: muted }}>
                          <th style={{ padding: '12px 14px' }}>Observation</th>
                          <th style={{ padding: '12px 14px' }}>Category</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedItems.map((row) => {
                          const active = row.id === selectedId;
                          const style = categoryStyle(row.uiBucket);
                          return (
                            <tr
                              key={row.id}
                              onClick={() => setSelectedId(row.id)}
                              style={{
                                borderBottom: `1px solid ${rowBorder}`,
                                color: text,
                                cursor: 'pointer',
                                backgroundColor: active
                                  ? isDarkMode
                                    ? 'rgba(234, 179, 8, 0.08)'
                                    : 'rgba(202, 138, 4, 0.06)'
                                  : 'transparent',
                              }}
                            >
                              <td style={{ padding: '12px 14px', fontWeight: 600 }}>{row.name || '—'}</td>
                              <td style={{ padding: '12px 14px' }}>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: '999px',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    ...style,
                                  }}
                                >
                                  {row.uiBucket || row.category || 'Other'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <TablePagination
                  currentPage={pageSafe}
                  setCurrentPage={setCurrentPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  totalItems={filteredItems.length}
                />
              </div>

              <DetailPanel
                selected={selected}
                selectedIndex={selectedIndex}
                filteredCount={filteredItems.length}
                onPrev={() => {
                  if (selectedIndex > 0) setSelectedId(filteredItems[selectedIndex - 1].id);
                }}
                onNext={() => {
                  if (selectedIndex >= 0 && selectedIndex < filteredItems.length - 1) {
                    setSelectedId(filteredItems[selectedIndex + 1].id);
                  }
                }}
                cardBg={cardBg}
                cardBorder={cardBorder}
                titleColor={titleColor}
                muted={muted}
                text={text}
                headBg={headBg}
                isDarkMode={isDarkMode}
              />
            </div>
          </>
        )}
      </div>
    </ConstructionLayout>
  );
}

function SummaryCard({
  label,
  count,
  icon: Icon,
  color,
  cardBg,
  cardBorder,
  titleColor,
  muted,
  onClick,
  active,
  accent,
  showView,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        backgroundColor: cardBg,
        border: `1px solid ${active ? accent : cardBorder}`,
        borderRadius: '12px',
        padding: '14px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Icon size={16} color={color} />
        {showView && (
          <span style={{ fontSize: '11px', color: muted }}>
            View →
          </span>
        )}
      </div>
      <div style={{ fontSize: '24px', fontWeight: 700, color: titleColor, lineHeight: 1 }}>
        {count}
      </div>
      <div style={{ fontSize: '12px', color: muted, fontWeight: 600 }}>{label}</div>
    </button>
  );
}

function DetailPanel({
  selected,
  selectedIndex,
  filteredCount,
  onPrev,
  onNext,
  cardBg,
  cardBorder,
  titleColor,
  muted,
  text,
  headBg,
  isDarkMode,
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setLightboxOpen(false);
    setImageFailed(false);
  }, [selected?.id]);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen]);

  if (!selected) {
    return (
      <div
        style={{
          backgroundColor: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: '12px',
          padding: '24px',
          color: muted,
          fontSize: '13px',
          minHeight: '280px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        Select an observation to view details.
      </div>
    );
  }

  const style = categoryStyle(selected.uiBucket);
  const details = selected.details;
  const previewUrl = resolveObservationMediaUrl(details?.previewPath || details?.filePath);
  const fullUrl = resolveObservationMediaUrl(details?.filePath || details?.previewPath) || previewUrl;
  const hasImage = Boolean(previewUrl) && !imageFailed;
  const MetaIcon = BUCKET_META[selected.uiBucket]?.icon || Info;
  const riskText = (details?.description || selected.description || '').trim();
  const statusLabel = (details?.status || '').trim() || '—';
  const hasAiEnrichment = Boolean(details?.risk || details?.recommendedAction);

  return (
    <div
      style={{
        backgroundColor: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: '12px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: '10px', minWidth: 0 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: style.backgroundColor,
              flexShrink: 0,
            }}
          >
            <MetaIcon size={16} color={style.color} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: titleColor }}>{selected.name || '—'}</div>
            <div style={{ fontSize: '12px', color: muted, marginTop: 2 }}>
              Observation ID #{selected.refId ?? '—'} | Detected from drone imagery
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: muted, fontSize: '12px', flexShrink: 0 }}>
          <button
            type="button"
            onClick={onPrev}
            disabled={selectedIndex <= 0}
            style={{
              background: 'none',
              border: 'none',
              color: muted,
              cursor: selectedIndex <= 0 ? 'default' : 'pointer',
              opacity: selectedIndex <= 0 ? 0.4 : 1,
            }}
          >
            <ChevronLeft size={16} />
          </button>
          <span>
            {selectedIndex + 1} of {filteredCount}
          </span>
          <button
            type="button"
            onClick={onNext}
            disabled={selectedIndex < 0 || selectedIndex >= filteredCount - 1}
            style={{
              background: 'none',
              border: 'none',
              color: muted,
              cursor: selectedIndex >= filteredCount - 1 ? 'default' : 'pointer',
              opacity: selectedIndex >= filteredCount - 1 ? 0.4 : 1,
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '4 / 3',
          borderRadius: '10px',
          backgroundColor: headBg,
          border: `1px solid ${cardBorder}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          color: muted,
          fontSize: '13px',
        }}
      >
        {hasImage ? (
          <>
            <img
              src={previewUrl}
              alt={selected.name || 'Observation'}
              onError={() => setImageFailed(true)}
              onClick={() => setLightboxOpen(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }}
            />
            <button
              type="button"
              title="Expand image"
              onClick={() => setLightboxOpen(true)}
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                width: 32,
                height: 32,
                borderRadius: '8px',
                border: `1px solid ${cardBorder}`,
                backgroundColor: isDarkMode ? 'rgba(17, 22, 34, 0.85)' : 'rgba(255,255,255,0.9)',
                color: titleColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Maximize2 size={14} />
            </button>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '16px' }}>
            <ImageOff size={28} color={muted} />
            <div style={{ textAlign: 'center' }}>
              {previewUrl && imageFailed
                ? 'Observation image could not be loaded'
                : 'No observation image available'}
            </div>
            {!details && (
              <div style={{ fontSize: '11px', color: muted, textAlign: 'center', maxWidth: 220 }}>
                AI enrichment details have not been attached for this observation yet.
              </div>
            )}
          </div>
        )}
      </div>

      <DetailField label="Category" muted={muted}>
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '999px',
            fontSize: '11px',
            fontWeight: 600,
            ...style,
          }}
        >
          {selected.uiBucket || selected.category || 'Other'}
        </span>
      </DetailField>
      <DetailField label="Location" muted={muted} text={text}>
        {formatLocation(selected)}
      </DetailField>
      <DetailField label="Risk" muted={muted} text={text} empty={!riskText}>
        {riskText || 'No risk assessment yet'}
      </DetailField>
      <DetailField label="Recommended Action" muted={muted} text={text} empty={!details?.recommendedAction}>
        {details?.recommendedAction || 'No recommended action yet'}
      </DetailField>
      <DetailField label="Evidence" muted={muted} text={text} empty={!hasImage}>
        {hasImage
          ? details?.caption || 'Drone imagery'
          : 'No evidence image attached'}
      </DetailField>
      <DetailField label="Status" muted={muted} text={text}>
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '999px',
            fontSize: '11px',
            fontWeight: 600,
            ...statusStyle(statusLabel, isDarkMode),
          }}
        >
          {statusLabel}
        </span>
      </DetailField>

      {!hasAiEnrichment && details && (
        <div style={{ fontSize: '11px', color: muted }}>
          Image is available; AI text fields are still empty for this observation.
        </div>
      )}

      {lightboxOpen && fullUrl && (
        <ImageLightbox
          src={fullUrl}
          alt={selected.name || 'Observation'}
          onClose={() => setLightboxOpen(false)}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
}

function ImageLightbox({ src, alt, onClose, isDarkMode }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Observation image"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        backgroundColor: 'rgba(0, 0, 0, 0.82)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          width: 40,
          height: 40,
          borderRadius: '10px',
          border: '1px solid rgba(255,255,255,0.2)',
          backgroundColor: 'rgba(17, 22, 34, 0.9)',
          color: '#F3F4F6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <X size={18} />
      </button>
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 'min(96vw, 1200px)',
          maxHeight: '90vh',
          objectFit: 'contain',
          borderRadius: '8px',
          boxShadow: isDarkMode ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.35)',
        }}
      />
    </div>
  );
}

function DetailField({ label, children, muted, text, empty }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div
        style={{
          fontSize: '11px',
          fontWeight: 600,
          color: muted || '#9CA3AF',
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '13px',
          color: empty ? muted || '#9CA3AF' : text || '#E5E7EB',
          fontStyle: empty ? 'italic' : 'normal',
          lineHeight: 1.45,
        }}
      >
        {children}
      </div>
    </div>
  );
}
